/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { FormattedMessage } from 'react-intl';

const propTypes = {
  title: PropTypes.any,
  input: PropTypes.object,
  meta: PropTypes.object,
  disabled: PropTypes.bool,
  onSuggestSelected: PropTypes.func,
  onSuggestCleared: PropTypes.func,
  suggestionType: PropTypes.oneOf(['address', 'city']),
  countryRestriction: PropTypes.string, // "none" or country code
  clearSuggestOnFocus: PropTypes.bool,
  forceSelectionFromDropdown: PropTypes.bool,
};

const defaultProps = {
  suggestionType: 'address',
  countryRestriction: COUNTRY,
};

class InputGoogleLocation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isSuggestsHidden: true,
      suggests: [],
      highlightedSuggestIndex: null,
    };
    if (props.forceSelectionFromDropdown) {
      this.state.wasSuggestionChosen = false;
    }

    this.handleChange = this.handleChange.bind(this);
    this.searchSuggests = this.searchSuggests.bind(this);
    this.selectSuggest = this.selectSuggest.bind(this);
    this.onSuggestSelected = this.onSuggestSelected.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleItemMouseDown = this.handleItemMouseDown.bind(this);
    this.handleItemMouseOut = this.handleItemMouseOut.bind(this);
  }

  UNSAFE_componentWillMount() {
    if (typeof window === 'undefined') {
      return;
    }

    const googleMaps = (window.google && window.google.maps) || this.googleMaps;
    if (!googleMaps) {
      if (console) {
        console.error('Google map api was not found in the page.');
      }
      return;
    }
    this.googleMaps = googleMaps;
    this.autocompleteService = new googleMaps.places.AutocompleteService();
    this.geocoder = new googleMaps.Geocoder();
  }

  onSuggestSelected(address) {
    this.setState({
      isSuggestsHidden: true,
      highlightedSuggestIndex: null,
    });

    let inputValue = address.formatted_address;
    if (this.props.suggestionType === 'city' && address.initialPrediction) {
      inputValue = address.initialPrediction.description || inputValue;
    }
    this.props.input.onChange(inputValue);

    if (this.props.onSuggestSelected) {
      this.props.onSuggestSelected(address);
    }
  }

  selectSuggest(suggest) {
    if (this.props.forceSelectionFromDropdown) {
      this.setState({ wasSuggestionChosen: true });
    }
    if (this.state.resultsGeocoded) {
      this.onSuggestSelected(suggest);
      return;
    }

    const options = {
      address: suggest.description,
    };

    this.geocoder.geocode(
      options,
      (results) => {
        if (results.length > 1) {
          this.setState({
            suggests: results,
            resultsGeocoded: true,
          });
        } else if (results.length === 1) {
          this.onSuggestSelected({
            ...results[0],
            initialPrediction: suggest,
          });
        }
      }
    );
  }

  // eslint-disable-next-line react/sort-comp -- node upgrade
  handleChange(event) {
    const eventTargetValue = event.target.value;
    if (!eventTargetValue.length) {
      this.setState({
        suggests: [],
        highlightedSuggestIndex: null,
        isActive: false,
        isSuggestsHidden: true,
      });
    } else {
      this.setState(
        {
          isActive: true,
          isSuggestsHidden: false,
        },
        () => this.searchSuggests(eventTargetValue)
      );
    }
    this.props.input.onChange(eventTargetValue);
  }

  handleFocus(event) {
    if (this.props.input.value.length) {
      this.setState({
        suggests: [],
        highlightedSuggestIndex: null,
        isActive: false,
        isSuggestsHidden: true,
      });
      if (this.props.clearSuggestOnFocus) {
        this.props.input.onChange('');
        if (this.props.onSuggestCleared) {
          this.props.onSuggestCleared();
        }
      }
    }
    this.props.input.onFocus(event);
  }

  handleKeyDown(event) {
    const { keyCode } = event;
    const { state } = this;
    let newHighlightedSectionIndex;
    switch (keyCode) {
      // ArrowUp
      case 38:
        if (state.highlightedSuggestIndex !== null
          && state.highlightedSuggestIndex < state.suggests.length
          && state.highlightedSuggestIndex > 0) {
          newHighlightedSectionIndex = state.highlightedSuggestIndex - 1;
        } else {
          newHighlightedSectionIndex = state.suggests.length - 1;
        }
        this.setState({ highlightedSuggestIndex: newHighlightedSectionIndex });
        event.preventDefault();
        break;

      // ArrowDown
      case 40:
        if (state.highlightedSuggestIndex !== null
          && state.highlightedSuggestIndex < state.suggests.length - 1) {
          newHighlightedSectionIndex = state.highlightedSuggestIndex + 1;
        } else {
          newHighlightedSectionIndex = 0;
        }
        this.setState({ highlightedSuggestIndex: newHighlightedSectionIndex });
        event.preventDefault();
        break;

      // Enter
      case 13: {
        this.setState({
          isSuggestsHidden: true,
          highlightedSuggestIndex: null,
        });
        this.selectSuggest(state.suggests[state.highlightedSuggestIndex]);
        event.preventDefault();
        break;
      }

      // Escape
      case 27: {
        this.setState({
          isSuggestsHidden: true,
          highlightedSuggestIndex: null,
        });
        event.preventDefault();
        break;
      }
      default:
    }
  }

  handleBlur(event) {
    if (!this.state.ignoreBlur) {
      this.setState({
        isSuggestsHidden: true,
        highlightedSuggestIndex: null,
        isActive: false,
      });
    }
    this.props.input.onBlur(event);
    if (this.props.forceSelectionFromDropdown) {
      if (!this.state.wasSuggestionChosen) {
        this.props.input.onChange('');
      }
      this.setState({ wasSuggestionChosen: false });
    }
  }

  handleItemMouseDown() {
    this.setState({ ignoreBlur: true });
  }

  handleItemMouseOut() {
    this.setState({ ignoreBlur: false });
  }

  searchSuggests(value) {
    if (!value) return;

    const options = {
      input: value,
    };

    const { countryRestriction, suggestionType } = this.props;

    if (countryRestriction !== 'none') {
      options.componentRestrictions = { country: countryRestriction };
    }
    if (suggestionType === 'city') {
      options.types = ['(cities)'];
    } else {
      options.types = ['address'];
    }

    this.autocompleteService.getPlacePredictions(
      options,
      (suggests) => {
        this.setState({
          suggests,
          isSuggestsHidden: false,
          resultsGeocoded: false,
        });
      }
    );
  }

  render() {
    const {
      input,
      meta: { touched, error, active },
      disabled,
      title,
    } = this.props;

    const {
      highlightedSuggestIndex,
      suggests,
    } = this.state;

    const value = input.value;

    const listClasses = cx({
      geosuggest__list: true,
      invisible: this.state.isSuggestsHidden,
    });

    const isFocused = active && !disabled;
    const inputClasses = cx({
      'input-txt': true,
      'input-txt--width-full': true,
      'input-txt--state-active': this.state.isActive || value.length > 0,
      'input-txt--state-focused': isFocused,
      'input-txt--state-disabled': disabled,
      'input-txt--state-error': touched && error,
    });

    return (
      <div className="geosuggest">
        <label className={inputClasses}>
          {title
            && <span className="input-txt__title">{title}</span>}
          <input
            {...input}
            className="input-txt__field"
            value={value}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onKeyDown={this.handleKeyDown}
            autoComplete="nope"
          />
          <span className="input-txt__hover-indicator" />
          <span className="input-txt__focus-indicator" />
        </label>
        <ul className={listClasses}>
          {suggests && suggests.map((suggest, index) => {
            const listItemClasses = cx({
              'geosuggest__list-item': true,
              'geosuggest__list-item--active': index === highlightedSuggestIndex,
            });
            return (
              <li
                key={index}
                onClick={() => this.selectSuggest(suggest)}
                onMouseDown={this.handleItemMouseDown}
                onMouseUp={this.handleItemMouseOut}
                className={listItemClasses}
              >
                {suggest.description || suggest.formatted_address}
              </li>
            );
          })}
          {!suggests
            && (
            <li className="geosuggest__list-item">
              <FormattedMessage id="customer.address.suggest.no_results" />
            </li>
            )}
        </ul>
      </div>
    );
  }
}

InputGoogleLocation.propTypes = propTypes;
InputGoogleLocation.defaultProps = defaultProps;

export default InputGoogleLocation;
